import './index.css';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ServicesSubMenu from '../ServicesSubMenu';
import ServicesPricingCTA from '../ServicesPricingCTA';
import Portfolio from '../Portfolio';
import beYouChatbotAvatar from '../../assets/images/portfolio/beyou-chatbot-avatar-poses.png';
import arkieFrontSparklesImage from '../../assets/images/arkiebyte/arkiebyte-front-sparkles.png';

const websiteServices = [
  {
    title: 'Static Website',
    description:
      'An entry-level professional web presence with a contact page that links directly to your email. This package can be linked to Google, includes search engine optimization, is cached in the cloud for very fast loading, and keeps monthly hosting fees low.',
  },
  {
    title: 'Cost-Efficient Cloud Website Hosting',
    description:
      'Host your website on reliable cloud infrastructure with HTTPS, fast content delivery, monitoring, backups, and practical monthly costs sized for a Northwest Arkansas small business.',
  },
  {
    title: 'SMS Messaging',
    description:
      'Set your business up with SMS messaging so customers can stay in touch, ask questions, receive updates, and keep communication moving from your website.',
  },
  {
    title: 'Scheduling Support',
    description:
      'Add scheduling paths that help customers request appointments, start bookings, or connect with your existing calendar and business workflow.',
  },
  {
    title: 'Customer Communication',
    description:
      'Connect forms, calls, texts, and follow-up workflows so your website becomes a practical communication hub instead of a static page.',
  },
  {
    title: 'Email Domains',
    description:
      'Set up a professional business email domain with DNS verification, mailbox hosting, forwarding, and documentation so your messages look trustworthy and reach the inbox.',
    hasEmailDomainDetails: true,
  },
  {
    title: 'Customer Login',
    description:
      'Give customers a secure place to sign in, manage their profile, view account details, and connect more deeply with your business online.',
  },
  {
    title: 'E-Commerce and Payment Provider Integration',
    description:
      'Add product pages, checkout flows, payment provider integrations, and ordering paths that help customers buy or request services directly from your site.',
  },
  {
    title: 'Friendly Chatbots',
    description:
      'Add a friendly RAG-powered chatbot that answers questions in natural language, reflects your brand, captures knowledge gaps, and gets more useful as you update its content.',
    path: '/services/chatbot',
  },
  {
    title: 'AI Integrated Intelligent Chatbots',
    description:
      'Use AI-powered chatbots connected to your business information to provide smarter answers, qualify requests, and support more useful customer conversations.',
    path: '/services/chatbot',
  },
];

const ServicesWebsites = () => {
  const location = useLocation();
  const [isEmailDomainInfoOpen, setIsEmailDomainInfoOpen] = useState(false);

  useEffect(() => {
    if (location.hash !== '#why-email-domain') {
      return;
    }

    setIsEmailDomainInfoOpen(true);
    window.requestAnimationFrame(() => {
      document.getElementById('why-email-domain')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, [location.hash]);

  return (
    <div className="services-placeholder-page">
      <ServicesSubMenu />
      <section className="services-placeholder-hero">
        <p className="services-placeholder-kicker">Hogcloud Services</p>
        <h1>Professional Web Development and Hosting in Northwest Arkansas</h1>
        <p>
          Custom websites and cost-efficient cloud hosting for Northwest Arkansas
          small businesses, local brands, and service providers. Hogcloud builds
          practical, mobile-friendly sites with clear messaging, SEO, SMS,
          scheduling, customer logins, e-commerce, AI chatbots, and room to grow.
        </p>
      </section>

      <section className="website-services" aria-labelledby="website-services-title">
        <div className="website-projects-header">
          <p className="services-placeholder-kicker">Website Services</p>
          <h2 id="website-services-title">From static sites to intelligent customer tools</h2>
        </div>

        <div className="website-service-grid">
          {websiteServices.map((service) => (
            <article className="website-service-card" key={service.title}>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              {service.hasEmailDomainDetails && (
                <div className="website-email-domain" id="why-email-domain">
                  <button
                    type="button"
                    className="website-email-domain-toggle"
                    aria-expanded={isEmailDomainInfoOpen}
                    onClick={() => setIsEmailDomainInfoOpen((isOpen) => !isOpen)}
                  >
                    Why have an email domain?
                  </button>
                  {isEmailDomainInfoOpen && (
                    <div className="website-email-domain-details">
                      <div className="website-email-domain-lede">
                        <h4>Don&apos;t let a free email address keep your business in the junk folder.</h4>
                        <p>
                          Upgrade to a professional business domain, protect invoices from spam
                          filters, and keep using your existing setup with zero downtime.
                        </p>
                      </div>

                      <section aria-labelledby="email-domain-problem-title">
                        <h5 id="email-domain-problem-title">The hidden cost of free email</h5>
                        <p>
                          Using a free @gmail.com or @hotmail.com address for business can be a
                          delivery liability. Major email networks have upgraded security systems
                          to fight phishing and scams.
                        </p>
                        <p>
                          When invoicing software, estimating apps, or marketing platforms send
                          mail from a free account, the receiving network may see a mismatch
                          between the address and the sending server. That can send invoices to
                          junk mail or block them entirely.
                        </p>
                      </section>

                      <section aria-labelledby="email-domain-bridge-title">
                        <h5 id="email-domain-bridge-title">The invisible email bridge</h5>
                        <p>
                          You do not have to abandon the address customers already know. Hogcloud
                          can create a background bridge so old and new email both land in one
                          professional inbox.
                        </p>
                        <div className="website-email-flow" aria-label="Email forwarding flow">
                          <span>Old client mail</span>
                          <strong>Old Gmail</strong>
                          <em>Invisible bridge</em>
                          <strong>New inbox</strong>
                          <span>New client mail</span>
                          <strong>Custom domain</strong>
                          <em>Verified delivery</em>
                          <strong>New inbox</strong>
                        </div>
                      </section>

                      <section aria-labelledby="email-domain-benefits-title">
                        <h5 id="email-domain-benefits-title">How it works without changing everything</h5>
                        <ul>
                          <li>One central dashboard: your old account can forward traffic to the new mailbox.</li>
                          <li>Zero lost messages: older printed addresses can still reach your new inbox.</li>
                          <li>Keep your interface: your professional domain can often work inside the app you already like.</li>
                        </ul>
                      </section>

                      <div className="website-email-comparison" aria-label="Email domain comparison">
                        <div>
                          <strong>Free email risk</strong>
                          <span>Higher risk of spam or junk filtering.</span>
                        </div>
                        <div>
                          <strong>Professional domain benefit</strong>
                          <span>Verified identity improves inbox delivery and client trust.</span>
                        </div>
                        <div>
                          <strong>Cost</strong>
                          <span>Mailboxes can start around $1 to $6 per month.</span>
                        </div>
                      </div>

                      <section aria-labelledby="email-domain-tech-title">
                        <h5 id="email-domain-tech-title">Behind the scenes</h5>
                        <p>
                          Hogcloud configures DNS records that act like digital passport control
                          for your email domain.
                        </p>
                        <ul>
                          <li>
                            <strong>SPF (Sender Policy Framework):</strong> tells receiving mail
                            servers which services are allowed to send for your domain. For
                            example, if QuickBooks sends an invoice as billing@yourbusiness.com,
                            SPF lets the recipient know QuickBooks is on your approved sender list.
                          </li>
                          <li>
                            <strong>DKIM (DomainKeys Identified Mail):</strong> adds a hidden
                            signature to each message. For example, if a quote is sent from your
                            email provider, the receiving server can verify that the message really
                            came from your domain and was not changed on the way.
                          </li>
                          <li>
                            <strong>DMARC (Domain-based Message Authentication, Reporting, and Conformance):</strong>
                            tells receiving servers what to do when SPF or DKIM checks fail. For
                            example, if a scammer tries to send a fake invoice using your domain,
                            DMARC can tell Gmail or Microsoft to quarantine or reject that message.
                          </li>
                        </ul>
                      </section>

                      <section aria-labelledby="email-domain-gmail-title">
                        <h5 id="email-domain-gmail-title">What about using Gmail with another app?</h5>
                        <p>
                          A personal Gmail account can sometimes be connected to an invoicing app,
                          CRM, or other third-party sender through SMTP, and that setup may be made
                          to pass SPF and DKIM checks. The problem is that it is still a free
                          personal account, so Google can treat unusual business sending patterns as
                          suspicious.
                        </p>
                        <ul>
                          <li>
                            <strong>Strict sending limits:</strong> free Gmail accounts have low
                            daily SMTP relay limits, so invoice batches or newsletters can get cut off.
                          </li>
                          <li>
                            <strong>Account lockouts:</strong> if sending volume spikes, Google may
                            assume the account was compromised and lock the owner out of Gmail,
                            Drive, photos, and other Google services until identity is verified.
                          </li>
                          <li>
                            <strong>Setup friction:</strong> third-party access often requires app
                            passwords or security exceptions that can break when Google changes its
                            policies.
                          </li>
                        </ul>
                      </section>

                      <Link className="website-email-domain-cta" to="/contact">
                        Fix My Email Delivery Today
                      </Link>
                    </div>
                  )}
                </div>
              )}
              {service.title === 'SMS Messaging' && (
                <div className="website-sms-snapshot" aria-label="iPhone text message conversation example">
                  <div className="website-sms-phone">
                    <div className="website-sms-speaker" />
                    <div className="website-sms-screen">
                      <div className="website-sms-status">
                        <span>9:41</span>
                        <span>5G</span>
                      </div>
                      <div className="website-sms-contact">
                        <span>HC</span>
                        <strong>Hogcloud Demo</strong>
                      </div>
                      <div className="website-sms-thread">
                        <p className="website-sms-bubble website-sms-bubble-out">
                          Hi, what services do you offer for small businesses?
                        </p>
                        <p className="website-sms-bubble website-sms-bubble-in">
                          We help with websites, SMS messaging, scheduling, and customer tools.
                        </p>
                        <p className="website-sms-bubble website-sms-bubble-out">
                          Can I get info about a website and texting setup?
                        </p>
                        <p className="website-sms-bubble website-sms-bubble-in">
                          Absolutely. What kind of business are you building for?
                        </p>
                      </div>
                      <div className="website-sms-compose">
                        <span>Services for my salon...</span>
                        <button type="button" aria-label="Send message" tabIndex={-1}>
                          ↑
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {service.title === 'Customer Login' && (
                <div className="website-login-snapshot" aria-label="Simple customer login form example">
                  <div className="website-login-snapshot-header">
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className="website-login-snapshot-body">
                    <strong>Customer Login</strong>
                    <label>
                      <span>Email</span>
                      <input type="text" value="customer@email.com" readOnly tabIndex={-1} />
                    </label>
                    <label>
                      <span>Password</span>
                      <input type="password" value="password" readOnly tabIndex={-1} />
                    </label>
                    <button type="button" tabIndex={-1}>Sign In</button>
                  </div>
                </div>
              )}
              {service.title === 'Scheduling Support' && (
                <div className="website-calendar-snapshot" aria-label="Simple scheduling calendar example">
                  <div className="website-calendar-header">
                    <strong>June</strong>
                    <span>9:00 AM</span>
                  </div>
                  <div className="website-calendar-weekdays">
                    <span>M</span>
                    <span>T</span>
                    <span>W</span>
                    <span>T</span>
                    <span>F</span>
                  </div>
                  <div className="website-calendar-grid">
                    <span>15</span>
                    <span>16</span>
                    <span className="active">17</span>
                    <span>18</span>
                    <span>19</span>
                    <span>22</span>
                    <span className="selected">23</span>
                    <span>24</span>
                    <span>25</span>
                    <span>26</span>
                  </div>
                </div>
              )}
              {service.title === 'Friendly Chatbots' && (
                <div className="website-chatbot-snapshot" aria-label="BeYouStudio23 salon chatbot preview">
                  <div className="website-chatbot-snapshot-header">
                    <span
                      className="website-chatbot-avatar"
                      style={{ backgroundImage: `url(${beYouChatbotAvatar})` }}
                    />
                    <div className="website-chatbot-snapshot-title">
                      <strong>SALON CHAT</strong>
                      <span>Ask me anything quick.</span>
                    </div>
                  </div>
                  <div className="website-chatbot-message">
                    Hi, I&apos;m the friendly chat helper. I can answer quick
                    questions about booking, services, location, and contact info.
                  </div>
                  <div className="website-chatbot-actions" aria-hidden="true">
                    <span>Book</span>
                    <span>Location</span>
                    <span>Services</span>
                  </div>
                  <strong>Try it out on our homepage</strong>
                  <Link className="website-chatbot-demo-link" to="/">
                    <img src={arkieFrontSparklesImage} alt="" aria-hidden="true" />
                    <span>Chat with ArkieByte</span>
                  </Link>
                </div>
              )}
              {service.path && (
                <Link className="website-service-more-link" to={service.path}>
                  See More
                </Link>
              )}
            </article>
          ))}
        </div>
      </section>

      <section className="website-projects" aria-labelledby="website-projects-title">
        <div className="website-projects-header">
          <p className="services-placeholder-kicker">Recent Website Work</p>
          <h2 id="website-projects-title">Local business sites</h2>
        </div>

        <Portfolio />
      </section>

      <ServicesPricingCTA />
    </div>
  );
};

export default ServicesWebsites;
